import { z } from "zod";

const providerSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);

export const browserSafeSummarySchema = z
  .record(z.string(), z.unknown())
  .superRefine((value, context) => {
    const sensitivePath = findSensitiveBrowserField(value);
    if (!sensitivePath) return;
    context.addIssue({
      code: "custom",
      message: "sensitive_browser_field",
      path: sensitivePath,
    });
  });

export const connectionResourceTypeSchema = z.enum([
  "shop",
  "ad_account",
  "email_account",
  "oauth_profile",
  "guild",
]);

export const organizationConnectionWireSchema = z
  .object({
    created_at: z.string().datetime({ offset: true }),
    display_name: z.string().max(240),
    external_subject: z.string().max(512),
    id: z.string().min(1).max(160),
    metadata_summary: browserSafeSummarySchema,
    organization_id: z.string().min(1).max(160),
    provider: providerSchema,
    resource_type: connectionResourceTypeSchema,
    status: z.enum(["pending", "active", "failed", "disconnected"]),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();

export const organizationConnectionsResponseSchema = z
  .object({ connections: z.array(organizationConnectionWireSchema) })
  .strict()
  .transform((response) => ({
    connections: response.connections.map((connection) => ({
      createdAt: connection.created_at,
      displayName: connection.display_name,
      externalSubject: connection.external_subject,
      id: connection.id,
      metadataSummary: connection.metadata_summary,
      organizationId: connection.organization_id,
      provider: connection.provider,
      resourceType: connection.resource_type,
      status: connection.status,
      updatedAt: connection.updated_at,
    })),
  }));

export const disconnectOrganizationConnectionResponseSchema = z
  .object({ connection: organizationConnectionWireSchema })
  .strict()
  .transform((response) =>
    organizationConnectionsResponseSchema.parse({
      connections: [response.connection],
    }).connections[0],
  );

export type OrganizationConnection = ReturnType<
  typeof organizationConnectionsResponseSchema.parse
>["connections"][number];

function findSensitiveBrowserField(
  value: unknown,
  path: PropertyKey[] = [],
): PropertyKey[] | null {
  if (Array.isArray(value)) {
    for (const [index, child] of value.entries()) {
      const found = findSensitiveBrowserField(child, [...path, index]);
      if (found) return found;
    }
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  for (const [key, child] of Object.entries(value)) {
    const nextPath = [...path, key];
    if (isSensitiveBrowserField(key)) return nextPath;
    const found = findSensitiveBrowserField(child, nextPath);
    if (found) return found;
  }
  return null;
}

function isSensitiveBrowserField(key: string) {
  const normalized = key.trim().toLowerCase();
  return (
    normalized === "secret" ||
    normalized === "token" ||
    normalized === "authorization_code" ||
    normalized === "auth_code" ||
    normalized === "oauth_code" ||
    normalized.includes("access_token") ||
    normalized.includes("refresh_token") ||
    normalized.includes("client_secret") ||
    normalized.includes("password") ||
    normalized.includes("credential_ref") ||
    normalized.includes("secret_ref") ||
    normalized.includes("shop_cipher")
  );
}
